import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, message, Spin } from 'antd';
import orgManagementService from '../../apiServices/orgManagementService';

interface AddUserToOrgModalProps {
    visible: boolean;
    onCancel: () => void;
    onSuccess: () => void;
    orgId: string;
    orgName: string;
}

interface AvailableUser {
    id: number;
    username: string;
    emailId: string;
    userRole: string;
}

const AddUserToOrgModal: React.FC<AddUserToOrgModalProps> = ({
    visible,
    onCancel,
    onSuccess,
    orgId,
    orgName,
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [fetchingUsers, setFetchingUsers] = useState(false);
    const [availableUsers, setAvailableUsers] = useState<AvailableUser[]>([]);

    // Fetch users when modal becomes visible
    useEffect(() => {
        if (visible) {
            fetchAvailableUsers();
        }
    }, [visible, orgId]);

    const fetchAvailableUsers = async () => {
        setFetchingUsers(true);
        try {
            // Fetch all users from the system
            const allUsersResponse = await orgManagementService.fetchUsers();

            // Fetch users already in this organization
            const orgUsersResponse = await orgManagementService.getOrgUsers(orgId);

            if (allUsersResponse?.meta?.code === 200 && allUsersResponse.data) {
                // Create a Set of user IDs already in the org for quick lookup
                const orgUserIds = new Set(orgUsersResponse.map(user => user.userId));

                // Filter to get only users NOT in this organization
                const usersNotInOrg = allUsersResponse.data.filter(
                    user => !orgUserIds.has(user.id) && user.userType !== 'external'
                );

                setAvailableUsers(usersNotInOrg);

                if (usersNotInOrg.length === 0) {
                    message.info('All users are already assigned to this account');
                }
            } else {
                message.warning('Failed to load available users');
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            message.error('Failed to load available users');
        } finally {
            setFetchingUsers(false);
        }
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            // Get the selected user's current accessible orgs
            const selectedUser = availableUsers.find(u => u.id === values.userId);
            if (!selectedUser) {
                message.error('Selected user not found');
                return;
            }

            // Call updateUserAccess to add this org to the user's accessible orgs
            await orgManagementService.updateUserAccess({
                userId: values.userId,
                orgIds: [orgId], // Add the current org to their access
            });

            message.success(`User assigned to ${orgName} successfully`);
            form.resetFields();
            onSuccess();
        } catch (error: any) {
            console.error('Error assigning user:', error);
            if (error?.errorFields) {
                // Form validation error
                return;
            }
            message.error(error?.message || 'Failed to assign user to account');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    return (
        <Modal
            title={`Assign User to ${orgName}`}
            open={visible}
            onOk={handleOk}
            onCancel={handleCancel}
            confirmLoading={loading}
            okText="Assign User"
            okButtonProps={{ disabled: availableUsers.length === 0 }}
        >
            <Spin spinning={fetchingUsers} tip="Loading available users...">
                <Form
                    form={form}
                    layout="vertical"
                >
                    <Form.Item
                        name="userId"
                        label="Select User"
                        rules={[{ required: true, message: 'Please select a user' }]}
                        help={availableUsers.length === 0 ? 'No users available to assign' : `${availableUsers.length} user(s) available`}
                    >
                        <Select
                            placeholder="Select a user to assign"
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={availableUsers.map(user => ({
                                value: user.id,
                                label: `${user.username} (${user.emailId})`,
                                user: user,
                            }))}
                            disabled={availableUsers.length === 0}
                        />
                    </Form.Item>

                    {form.getFieldValue('userId') && (
                        <div className="bg-blue-50 p-3 rounded mb-2">
                            <p className="text-sm text-gray-600">
                                <strong>Note:</strong> The selected user will be assigned to {orgName} with their existing role.
                            </p>
                        </div>
                    )}
                </Form>
            </Spin>
        </Modal>
    );
};

export default AddUserToOrgModal;
